/**
 * MonthlyIncomeBreakdown.mask.ts
 *
 * Data-masking logic for the monthly income breakdown tiles.
 * When showData is false, all visible value fields in IncomeTileConfig
 * are replaced with "--" so no real data is visible.
 */

import type { IncomeTileConfig } from "../IncomeBreakdown/IncomeBreakdown.types";
import type { AWARD_PROGRESS } from "../../../../common/enums/award-progress-codes";

const MASKED_VALUE = "--";

const maskTile = (tile: IncomeTileConfig): IncomeTileConfig => ({
  ...tile,
  header: tile.header
    ? {
        ...tile.header,
        value: MASKED_VALUE,
        link: tile.header.link === "#ruby-bonus-modal" ? "" : tile.header.link,
        showArrow: tile.header.link === "#ruby-bonus-modal" ? false : tile.header.showArrow,
      }
    : undefined,
  linkRows: tile.linkRows
    ? { items: tile.linkRows.items.map((item) => ({ ...item, value: MASKED_VALUE, link: "", disabled: true })) }
    : undefined,
  metricCard: tile.metricCard
    ? { items: tile.metricCard.items.map((item) => ({ ...item, value: MASKED_VALUE })) }
    : undefined,
  legsProgress: tile.legsProgress
    ? { items: tile.legsProgress.items.map((item) => ({ ...item, value: MASKED_VALUE })) }
    : undefined,
  kpiTrackers: tile.kpiTrackers
    ? {
        items: tile.kpiTrackers.items.map((item) => ({
          ...item,
          percentage: 0,
          achievedSegments: 0,
          isMet: false,
          label: MASKED_VALUE,
        })),
      }
    : undefined,
  pvRequirement: tile.pvRequirement ? { ...tile.pvRequirement, percent: 0, label: MASKED_VALUE } : undefined,
  pvRequirements: tile.pvRequirements
    ? tile.pvRequirements.map((p) => ({ ...p, percent: 0, label: MASKED_VALUE }))
    : undefined,
  pinRequirement: tile.pinRequirement
    ? {
        ...tile.pinRequirement,
        value: MASKED_VALUE,
        met: false,
        status: "" as AWARD_PROGRESS,
        statusTag: undefined,
        label: MASKED_VALUE,
      }
    : undefined,
  pinRequirements: tile.pinRequirements
    ? tile.pinRequirements.map((p) => ({
        ...p,
        value: MASKED_VALUE,
        met: false,
        status: "" as AWARD_PROGRESS,
        statusTag: undefined,
        label: MASKED_VALUE,
      }))
    : undefined,
  stackedTiles: tile.stackedTiles
    ? tile.stackedTiles.map((st) => ({
        ...st,
        header: st.header ? { ...st.header, value: MASKED_VALUE } : undefined,
      }))
    : undefined,
  subTiles: tile.subTiles ? tile.subTiles.map(maskTile) : undefined,
});

export const maskIncomeTiles = (
  tiles: IncomeTileConfig[] | undefined,
  showData: boolean,
): IncomeTileConfig[] | undefined => {
  if (showData || !tiles) return tiles;
  return tiles.map(maskTile);
};

export interface MappedIncomeData {
  totalIncome: string;
  corePlanTiles: IncomeTileConfig[] | undefined;
  corePlanLeftTiles: IncomeTileConfig[] | undefined;
  corePlanRightTiles: IncomeTileConfig[] | undefined;
  corePlusTiles: IncomeTileConfig[] | undefined;
  [key: string]: unknown;
}

export const maskMappedIncomeData = <T extends MappedIncomeData>(data: T | null, showData: boolean): T | null => {
  if (!data) return null;
  if (showData) return data;
  return {
    ...data,
    totalIncome: MASKED_VALUE,
    corePlanTiles: maskIncomeTiles(data.corePlanTiles, false),
    corePlanLeftTiles: maskIncomeTiles(data.corePlanLeftTiles, false),
    corePlanRightTiles: maskIncomeTiles(data.corePlanRightTiles, false),
    corePlusTiles: maskIncomeTiles(data.corePlusTiles, false),
  };
};
